import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import type { Container } from "@egalregal/shared";
import { LabelCell } from "../../shared/components/LabelCell";
import type { LabelConfig } from "../../shared/components/LabelCell";

/** Fresh UUID for a pre-printed label; Web Crypto is available in browsers and jsdom. */
function newLabelId(): string {
  return crypto.randomUUID();
}

/**
 * The label sheet (ticket 09, columns × rows grid in ticket 06): a grid of
 * label cells, one per container. Pure and shared by the live preview and the
 * print-only portal; the QR encodes the plain UUID (ADR-0003). Each cell shows
 * the short-UUID icons, QR, the short UUID and the container name when there
 * is one (ADR-0027). With `blankCount` (ticket 30) it renders that many fresh
 * UUIDs — pre-printed labels for containers that don't exist yet. The UUIDs
 * (and their icons) are minted on mount and extended/truncated when the count
 * changes; nothing is stored server-side. The sheet fills the fixed DIN A4
 * print width, so more columns = smaller QR label cards.
 */
export function PrintSheet({
  containers,
  columns,
  rows,
  blankCount,
  config,
}: {
  containers: Container[];
  columns: number;
  rows: number;
  /** Blank-labels mode (ticket 30): print this many fresh QR-only labels. */
  blankCount?: number;
  config?: LabelConfig;
}) {
  const [blankUuids, setBlankUuids] = useState(() =>
    blankCount ? Array.from({ length: blankCount }, () => newLabelId()) : [],
  );

  useEffect(() => {
    setBlankUuids((prev) => {
      const target = blankCount ?? 0;
      if (prev.length === target) return prev;
      if (prev.length > target) return prev.slice(0, target);
      return [...prev, ...Array.from({ length: target - prev.length }, () => newLabelId())];
    });
  }, [blankCount]);

  const cells = blankCount
    ? blankUuids.map((uuid) => <LabelCell key={uuid} uuid={uuid} name="" className="print-cell" config={config} columns={columns} />)
    : containers.map((c) => <LabelCell key={c.id} uuid={c.id} name={c.name} className="print-cell" config={config} columns={columns} />);

  return (
    <Box
      className="print-sheet"
      data-columns={columns}
      data-rows={rows}
      sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1 }}
    >
      {cells}
    </Box>
  );
}